import { z } from "zod"
import { CreateMembershipTierDtoSchema } from "@mimber/schema"

/**
 * Helper to coerce string inputs to numbers
 */
const numberFromString = z
  .union([z.number(), z.string()])
  .transform((val) =>
    typeof val === "string" && val !== "" ? Number(val) : val
  )
  .pipe(z.number())

const optionalNumberFromString = z
  .union([z.number(), z.string(), z.undefined(), z.null()])
  .transform((val) => {
    if (val === "" || val === undefined || val === null) return undefined
    return typeof val === "string" ? Number(val) : val
  })
  .pipe(
    z
      .number()
      .int("Price must be an integer number of cents")
      .min(0, "Price must be non-negative")
      .optional()
  )

const optionalIntervalLengthFromString = z
  .union([z.number(), z.string(), z.undefined(), z.null()])
  .transform((val) => {
    if (val === "" || val === undefined || val === null) return undefined
    return typeof val === "string" ? Number(val) : val
  })
  .pipe(
    z
      .number()
      .int("Interval length must be an integer")
      .min(1, "Interval length must be at least 1")
      .optional()
  )

/**
 * Form schema that extends the DTO schema with string-to-number transformations
 * for form inputs (text fields return strings that need to be converted)
 */
export const createMembershipTierSchema = CreateMembershipTierDtoSchema.extend({
  level: numberFromString.pipe(
    z
      .number()
      .int("Level must be an integer")
      .min(1, "Level must be at least 1")
  ),
  price: optionalNumberFromString,
  intervalLength: optionalIntervalLengthFromString,
  description: z
    .string()
    .max(1000, "Description must be less than 1000 characters")
    .optional()
    .or(z.literal(""))
    .transform((val) => (val === "" ? undefined : val)),
})

/**
 * Output type after transformation - this is what gets passed to onSubmit
 */
export type CreateMembershipTierData = z.output<
  typeof createMembershipTierSchema
>
